import { useEffect, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { NavLinkProvider, ThemeProvider, ToastProvider, TooltipProvider, UiLocaleProvider, type ThemePreference } from '@moods/ui';
import { ApiError } from '../lib/api';
import { usePrefs } from '../store/prefs';
import { PwaUpdateToast } from '../features/pwa/PwaUpdateToast';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
    mutations: { retry: false },
  },
});

/** App-wide providers: query cache, theme, locale for @moods/ui, tooltips, toasts, router-aware links. */
export function Providers({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [locale, setLocale] = useState<'tr' | 'en'>(i18n.language === 'en' ? 'en' : 'tr');
  useEffect(() => {
    const on = (lng: string) => setLocale(lng === 'en' ? 'en' : 'tr');
    i18n.on('languageChanged', on);
    return () => i18n.off('languageChanged', on);
  }, [i18n]);
  const theme = usePrefs(s => s.theme);
  const motion = usePrefs(s => s.motion);
  const setTheme = usePrefs(s => s.setTheme);
  const setMotion = usePrefs(s => s.setMotion);
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider preference={theme} motion={motion} onPreferenceChange={(p: ThemePreference) => setTheme(p)} onMotionChange={setMotion}>
        <UiLocaleProvider locale={locale}>
          <TooltipProvider>
            <ToastProvider>
              <NavLinkProvider renderLink={({ href, ref, ...p }) => <Link to={href} ref={ref} {...p} />}>
                {children}
                <PwaUpdateToast />
              </NavLinkProvider>
            </ToastProvider>
          </TooltipProvider>
        </UiLocaleProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
